import React, {useEffect, useState, useRef, FormEvent} from 'react';
import {message, Modal} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import LoginForm from '../../../molecules/Forms/Login/component';
import RegistrationForm from '../../../molecules/Forms/Registration/component';
import GeneralModal, {GeneralModalRef} from '../../../molecules/Modals/general/component';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import authService from '../../../../services/authService';
import * as authActions from '../../../../store/modules/auth/actions';
import {setCookie} from 'typescript-cookie';
import {getInvitedFromCookie, clearInvitedFromCookies} from '../../../../utils/cookiePartnerReferral';
import {validateEmail, validateUsername} from '../../../../utils/validators';
import ButtonComponent from '../../../atoms/Buttons';
import {getFingerprintData} from '../../../../utils/fingerprintAuth';
import './style.css';
import ResetPasswordDialog from "../ResetPassword/component";
import {useIsMobile} from '../../../../utils/helpers';
import {identifyUser} from '../../../../analytics/posthog';

type FormProps = {
    type: string;
    onClose: () => void;
    onOpenDialog: (mode: string) => void;
    lockedRoleType?: string;
};

const AuthDialog = ({type, onClose, onOpenDialog, lockedRoleType}: FormProps) => {
    const dispatch = useDispatch();
    const generalModalRef = useRef<GeneralModalRef>(null);
    const navigate = useNavigate();

    const location = useLocation();
    const invitedFrom = new URLSearchParams(location.search).get('invitedFrom') || getInvitedFromCookie() || undefined;

    // Login props
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [required, setRequired] = useState(false);

    // Signup props
    const [accountType, setAccountType] = useState<'individual' | 'business'>('individual');
    const [roleType, setRoleType] = useState('');
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    const [emailValid, setEmailValid] = useState(true);
    const [usernameError, setUsernameError] = useState<string | null>(null);

    // Profile props
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    // BusinessInfo props
    const [companyName, setCompanyName] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [vatNumber, setVatNumber] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');

    const [agreementChecked, setAgreementChecked] = useState(false);

    const [signupSuccessOpen, setSignupSuccessOpen] = useState(false);
    const [signupEmail, setSignupEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isMobile = useIsMobile();

    useEffect(() => {
        if (type === 'registration' && lockedRoleType) {
            setRoleType(lockedRoleType);
        }
    }, [type, lockedRoleType]);

    const handleEmailChange = (email: string) => setEmail(email);
    const handlePasswordChange = (password: string) => setPassword(password);

    const handleLogin = async () => {
        try {
            setRequired(true);
            const user = {identifier: email, password, metadata: await getFingerprintData('auth', {}) || ''};

            if (email && password) {
                const result = await authService.login(user);
                if (result.status === 200) {
                    identifyUser(result.data.userId, {
                        user_id: result.data.userId,
                        role: result.data.roles?.[0]?.roleType,
                        user_type: result.data.userType,
                        account_status: result.data.status
                    });
                    dispatch(authActions.loginSuccess(true, result.data));
                    setCookie('token', result.data.bearerToken, {
                        expires: 2 / 24,
                        path: '/',
                        sameSite: 'none',
                        secure: true
                    });
                    navigate('home');
                    resetRegistrationForm();
                    onClose();
                } else {
                    dispatch(authActions.loginSuccess(false, null));
                }
            }
        } catch (error: any) {
            dispatch(authActions.loginSuccess(false, null));
            message.error(error?.response?.data?.message);
        }
    };

    const prepareUserObject = async () => {
        const base: any = {
            username,
            email,
            roleType,
            userType: accountType,
            metadata: await getFingerprintData('auth', {}) || '',
        };

        if (invitedFrom) base.invitedFrom = invitedFrom;

        if (accountType === 'individual') {
            base.profile = {firstName, lastName, phone};
        } else {
            base.businessInfo = {companyName, companyWebsite, vatNumber, companyAddress, phone};
        }

        return base;
    };

    const handleRegistration = async () => {
        setRequired(true);

        const usernameValidation = validateUsername(username);
        if (!usernameValidation.valid) {
            setUsernameError(usernameValidation.errors[0]);
            message.error(usernameValidation.errors[0]);
            return;
        }

        if (!agreementChecked) {
            message.error("You must agree to Promotify's Terms and Conditions and Privacy Policy before creating an account.");
            return;
        }

        if (
            (accountType === 'individual' && (!firstName || !lastName)) ||
            (accountType === 'business' && !companyName) ||
            !email ||
            !roleType
        ) {
            return;
        }

        try {
            handleEmailChangeRegistration(email);
            const user = await prepareUserObject();
            const result = await authService.signUp(user);
            if (result.status === 201) {
                clearInvitedFromCookies();
                setSignupEmail(email);
                resetRegistrationForm();
                setSignupSuccessOpen(true);
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message);
        }
    };

    // IMPORTANT: ButtonComponent expects (event: FormEvent) => Promise<void>
    const handleSubmit = async (e: FormEvent) => {
        e?.preventDefault?.();

        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            if (type === 'login') {
                await handleLogin();
            } else if (type === 'registration') {
                await handleRegistration();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFirstNameChange = (firstName: string) => setFirstName(firstName);
    const handleLastNameChange = (lastName: string) => setLastName(lastName);
    const handleRoleTypeChange = (type: string) => setRoleType(type);
    const handlePhoneChange = (phone: string) => setPhone(phone);
    const handleAgreementChange = (checked: boolean) => setAgreementChecked(checked);

    const resetRegistrationForm = () => {
        setEmail('');
        setPassword('');
        setUsername('');
        setRoleType(lockedRoleType || '');
        setFirstName('');
        setLastName('');
        setPhone('');
        setCompanyName('');
        setCompanyWebsite('');
        setVatNumber('');
        setCompanyAddress('');
        setRequired(false);
        setEmailValid(true);
        setAgreementChecked(false);
    };

    const handleEmailChangeRegistration = (email: string) => {
        setEmail(email);
        setEmailValid(validateEmail(email));
    };

    const handleUsernameChange = (value: string) => {
        const username = value.toLowerCase();
        setUsername(username);

        const result = validateUsername(username);
        setUsernameError(result.valid ? null : result.errors[0]);
    };

    if (type === 'reset') {
        return (
            <>
                <ResetPasswordDialog visible={true} onClose={onClose} email={email}/>
                <GeneralModal ref={generalModalRef}/>
            </>
        );
    }

    return (
        <>
            <Modal
                open={true}
                onCancel={onClose}
                centered
                closeIcon={<CloseOutlined/>}
                destroyOnClose={true}
                width={isMobile ? '92vw' : (type === 'login' ? 600 : 650)}
                className={accountType === 'business' ? 'businessModal' : 'customModal'}
                bodyStyle={
                    type === 'login'
                        ? {
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: isMobile ? 0 : 32
                        }
                        : {padding: 0}
                }
                footer={[
                    <ButtonComponent
                        key="create-button"
                        onClick={handleSubmit}
                        label={type === 'login' ? 'Log in' : 'Create user'}
                        buttonType="primary"
                        className={`buttonAuth ${isSubmitting ? 'buttonAuthDisabled' : ''}`}
                    />
                ]}
            >
                {type === 'login' && (
                    <LoginForm
                        email={email}
                        onEmailChange={handleEmailChange}
                        password={password}
                        required={required}
                        onPasswordChange={handlePasswordChange}
                        onSubmit={handleLogin}
                        onOpenDialog={onOpenDialog}
                        onClose={onClose}
                    />
                )}

                {type === 'registration' && (
                    <div className="registrationScrollWrapper">
                        <RegistrationForm
                            accountType={accountType}
                            onAccountTypeChange={setAccountType}
                            firstName={firstName}
                            onFirstName={handleFirstNameChange}
                            lastName={lastName}
                            onLastName={handleLastNameChange}
                            phone={phone}
                            onPhoneChange={handlePhoneChange}
                            companyName={companyName}
                            onCompanyNameChange={setCompanyName}
                            companyWebsite={companyWebsite}
                            onCompanyWebsiteChange={setCompanyWebsite}
                            vatNumber={vatNumber}
                            onVatNumberChange={setVatNumber}
                            companyAddress={companyAddress}
                            onCompanyAddressChange={setCompanyAddress}
                            username={username}
                            onUsernameChange={handleUsernameChange}
                            usernameError={usernameError}
                            roleType={roleType}
                            onRoleTypeChange={handleRoleTypeChange}
                            lockedRoleType={lockedRoleType}
                            email={email}
                            onEmailChange={handleEmailChangeRegistration}
                            emailValid={emailValid}
                            required={required}
                            onSubmit={handleRegistration}
                            onOpenDialog={onOpenDialog}
                            onClose={onClose}
                            agreementChecked={agreementChecked}
                            onAgreementChange={handleAgreementChange}
                        />
                    </div>
                )}

                <GeneralModal ref={generalModalRef}/>
            </Modal>

            <Modal
                open={signupSuccessOpen}
                centered
                destroyOnClose
                footer={null}
                closable={false}
                maskClosable={false}
                keyboard={false}
                width={isMobile ? '92vw' : 520}
                styles={{body: {padding: isMobile ? 16 : 24}}}
            >
                <div style={{textAlign: 'center'}}>
                    <div style={{fontSize: isMobile ? 18 : 20, fontWeight: 700, marginBottom: 10}}>
                        Account created
                    </div>

                    <div style={{fontSize: isMobile ? 13 : 14, color: '#666', lineHeight: 1.5}}>
                        We’ve sent a verification email to <b>{signupEmail}</b>.
                        <br/>
                        Please verify your email and set your password before logging in.
                    </div>

                    <div style={{marginTop: 16, fontSize: isMobile ? 12 : 13, color: '#888'}}>
                        Tip: If you don’t see it, check your spam/junk folder.
                    </div>

                    <div
                        style={{
                            marginTop: 20,
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}
                    >
                        <ButtonComponent
                            label="OK"
                            buttonType="primary"
                            className="buttonAuth"
                            onClick={async (e: FormEvent) => {
                                e?.preventDefault?.();
                                setSignupSuccessOpen(false);
                                onClose();
                            }}
                        />

                        <button
                            type="button"
                            onClick={() => {
                                setSignupSuccessOpen(false);
                                onClose();
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                padding: isMobile ? '10px 0' : '8px 12px',
                                width: isMobile ? '100%' : 'auto',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AuthDialog;
